/* ==========================================================================
   Internal (Super-Admin) API Client
   Task F33: Token management + API functions for internal tools
   ========================================================================== */

import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ApiClientError } from './client'
import { isTwoFactorChallenge } from './types'
import type {
  ApiError,
  CsvImportResult,
  TwoFactorChallengeResponse,
  TwoFactorLoginRequest,
  TwoFactorStatusResponse,
  TwoFactorSetupResponse,
} from './types'
import { getTwoFactorDeviceToken, setTwoFactorDeviceToken } from '@/utils/twoFactorDevice'

/* --------------------------------------------------------------------------
   Configuration
   -------------------------------------------------------------------------- */

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

const INTERNAL_TOKEN_KEY = 'tjm_internal_token'
const INTERNAL_TOKEN_EXPIRY_KEY = 'tjm_internal_token_expires_at'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

export type SubscriptionTier = 'starter' | 'growth' | 'scale'
export type BillingCycle = 'monthly' | 'annual'
export type Currency = 'usd' | 'cad'

export interface CreateCheckoutLinkRequest {
  tier: SubscriptionTier
  billing_cycle: BillingCycle
  currency: Currency
  customer_email: string
  customer_name: string
}

export interface CreateCheckoutLinkResponse {
  checkout_url: string
}

interface TokenResponse {
  access_token: string
  expires_at: string
  two_factor_device_token?: string | null
}

/* --------------------------------------------------------------------------
   Token Management
   -------------------------------------------------------------------------- */

export function getInternalToken(): string | null {
  return localStorage.getItem(INTERNAL_TOKEN_KEY)
}

export function setInternalToken(token: string): void {
  localStorage.setItem(INTERNAL_TOKEN_KEY, token)
}

export function clearInternalToken(): void {
  localStorage.removeItem(INTERNAL_TOKEN_KEY)
  localStorage.removeItem(INTERNAL_TOKEN_EXPIRY_KEY)
}

export function isInternalTokenExpired(): boolean {
  const expiry = localStorage.getItem(INTERNAL_TOKEN_EXPIRY_KEY)
  if (!expiry) return true
  return new Date() >= new Date(new Date(expiry).getTime() - 30000)
}

/* --------------------------------------------------------------------------
   Error Handling
   -------------------------------------------------------------------------- */

function extractApiMessage(data: ApiError | null): string | null {
  if (!data) return null
  if (typeof data.detail === 'string') return data.detail
  if (Array.isArray(data.detail) && data.detail.length > 0) {
    return data.detail.map((err) => err.msg).join(', ')
  }
  return null
}

function transformError(error: AxiosError<ApiError>): ApiClientError {
  const status = error.response?.status || 0
  const data = error.response?.data || null
  const apiMessage = extractApiMessage(data)

  let message: string
  if (apiMessage) {
    message = apiMessage
  } else {
    switch (status) {
      case 401:
        message = 'Invalid credentials'
        break
      case 429:
        message = 'Too many requests. Please try again later.'
        break
      case 502:
        message = 'Failed to create checkout session. Please try again.'
        break
      case 0:
        message = 'Unable to connect to server. Please check your connection.'
        break
      default:
        message = error.message || 'An unexpected error occurred'
    }
  }

  return new ApiClientError(message, status, data)
}

/* --------------------------------------------------------------------------
   Axios Instance
   -------------------------------------------------------------------------- */

const internalClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 30000,
})

internalClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getInternalToken()
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

internalClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    const apiError = transformError(error)
    return Promise.reject(apiError)
  }
)

/* --------------------------------------------------------------------------
   Auth Functions
   -------------------------------------------------------------------------- */

function storeInternalSession(data: TokenResponse): void {
  setInternalToken(data.access_token)
  localStorage.setItem(INTERNAL_TOKEN_EXPIRY_KEY, data.expires_at)
}

/**
 * Super-admin login. When the account has 2FA on (PORTAL-187) nothing is stored
 * and the challenge is returned instead; finish with `internalLoginTwoFactor`.
 */
export async function internalLogin(
  email: string,
  password: string
): Promise<TokenResponse | TwoFactorChallengeResponse> {
  const response = await internalClient.post<TokenResponse | TwoFactorChallengeResponse>(
    '/internal/auth/login',
    {
      email,
      password,
      two_factor_device_token: getTwoFactorDeviceToken('internal', email),
    }
  )
  if (isTwoFactorChallenge(response.data)) {
    return response.data
  }

  storeInternalSession(response.data)

  return response.data
}

/** Second login step: challenge pass plus authenticator code (PORTAL-187). */
export async function internalLoginTwoFactor(
  request: TwoFactorLoginRequest,
  email: string
): Promise<TokenResponse> {
  const response = await internalClient.post<TokenResponse>(
    '/internal/auth/login/two-factor',
    request
  )
  storeInternalSession(response.data)
  setTwoFactorDeviceToken('internal', email, response.data.two_factor_device_token)
  return response.data
}

/* Two-factor settings for the signed-in super admin (PORTAL-187) */

export async function getInternalTwoFactorStatus(): Promise<TwoFactorStatusResponse> {
  const response = await internalClient.get<TwoFactorStatusResponse>(
    '/internal/auth/two-factor'
  )
  return response.data
}

export async function startInternalTwoFactorSetup(): Promise<TwoFactorSetupResponse> {
  const response = await internalClient.post<TwoFactorSetupResponse>(
    '/internal/auth/two-factor/setup'
  )
  return response.data
}

// Enabling does not remember this browser (that is opt-in at the login step), so
// the next sign-in asks for the code. Disabling needs no local cleanup: the server
// already rejects device tokens from a previous enrollment.
export async function enableInternalTwoFactor(code: string): Promise<TwoFactorStatusResponse> {
  const response = await internalClient.post<TwoFactorStatusResponse>(
    '/internal/auth/two-factor/enable',
    { code }
  )
  return response.data
}

export async function disableInternalTwoFactor(code: string): Promise<TwoFactorStatusResponse> {
  const response = await internalClient.post<TwoFactorStatusResponse>(
    '/internal/auth/two-factor/disable',
    { code }
  )
  return response.data
}

export async function internalLogout(): Promise<void> {
  const token = getInternalToken()
  try {
    if (token) {
      await internalClient.post('/internal/auth/logout', null, {
        headers: { Authorization: `Bearer ${token}` },
      })
    }
  } catch {
    // Ignore logout errors — clear local data regardless
  } finally {
    clearInternalToken()
  }
}

/* --------------------------------------------------------------------------
   Billing Functions
   -------------------------------------------------------------------------- */

export async function generateCheckoutLink(
  data: CreateCheckoutLinkRequest
): Promise<CreateCheckoutLinkResponse> {
  const response = await internalClient.post<CreateCheckoutLinkResponse>(
    '/internal/checkout-link',
    data
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Intake Types
   -------------------------------------------------------------------------- */

export type IntakeStatus =
  | 'submitted'
  | 'in_review'
  | 'provisioned'
  | 'live'
  | 'completed'

export interface IntakeListItem {
  id: string
  pharmacy_name: string
  admin_email: string
  fax_number: string
  preferred_slug: string
  status: IntakeStatus
  submitted_at: string
  tier: string | null
  billing_cycle: string | null
}

export interface IntakeListResponse {
  items: IntakeListItem[]
  total: number
  page: number
  page_size: number
}

export interface IntakeListFilters {
  status?: IntakeStatus
  search?: string
  page?: number
  page_size?: number
}

export interface IntakeDetail {
  id: string
  subscription_id: string
  pharmacy_name: string
  admin_email: string
  fax_number: string
  preferred_slug: string
  status: IntakeStatus
  submitted_at: string
  notes: string | null
  logo_file_url: string | null
  primary_color: string | null
  secondary_color: string | null
  formulary_file_url: string | null
  formulary_file_name: string | null
  tier: string | null
  billing_cycle: string | null
  stripe_customer_id: string | null
  pharmacy_id: string | null
  pharmacy_slug: string | null
}

export interface ProvisionResult {
  pharmacy_id: string
  pharmacy_slug: string
  admin_email: string
}

export interface AdminSessionResult {
  token: string
  pharmacy_slug: string
  admin_portal_url: string
  expires_at: string
}

/* --------------------------------------------------------------------------
   Intake Functions
   -------------------------------------------------------------------------- */

export async function listIntakeSubmissions(
  filters: IntakeListFilters = {}
): Promise<IntakeListResponse> {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.page_size) params.set('page_size', String(filters.page_size))

  const response = await internalClient.get<IntakeListResponse>(
    `/internal/intake?${params.toString()}`
  )
  return response.data
}

export async function getIntakeDetail(id: string): Promise<IntakeDetail> {
  const response = await internalClient.get<IntakeDetail>(
    `/internal/intake/${id}`
  )
  return response.data
}

export async function provisionFromIntake(
  id: string,
  slugOverride?: string
): Promise<ProvisionResult> {
  const body = slugOverride ? { slug_override: slugOverride } : undefined
  const response = await internalClient.post<ProvisionResult>(
    `/internal/intake/${id}/provision`,
    body
  )
  return response.data
}

export async function createAdminSession(
  id: string
): Promise<AdminSessionResult> {
  const response = await internalClient.post<AdminSessionResult>(
    `/internal/intake/${id}/admin-session`
  )
  return response.data
}

export async function markGoLive(id: string): Promise<IntakeDetail> {
  const response = await internalClient.post<IntakeDetail>(
    `/internal/intake/${id}/go-live`
  )
  return response.data
}

export async function markComplete(id: string): Promise<IntakeDetail> {
  const response = await internalClient.post<IntakeDetail>(
    `/internal/intake/${id}/complete`
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Pending Subscription Types (Awaiting Intake)
   -------------------------------------------------------------------------- */

export interface PendingSubscriptionItem {
  id: string
  contact_email: string
  tier: string
  billing_cycle: string
  status: string
  stripe_customer_id: string | null
  created_at: string
  intake_token: string | null
  intake_token_expires_at: string | null
  intake_token_active: boolean
}

export interface PendingSubscriptionListResponse {
  items: PendingSubscriptionItem[]
  total: number
  page: number
  page_size: number
}

export interface PendingSubscriptionFilters {
  search?: string
  page?: number
  page_size?: number
}

export interface ResendIntakeResult {
  intake_token: string
  intake_token_expires_at: string
  intake_url: string
}

/* --------------------------------------------------------------------------
   Pending Subscription Functions
   -------------------------------------------------------------------------- */

export async function listPendingSubscriptions(
  filters: PendingSubscriptionFilters = {}
): Promise<PendingSubscriptionListResponse> {
  const params = new URLSearchParams()
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', String(filters.page))
  if (filters.page_size) params.set('page_size', String(filters.page_size))

  const response = await internalClient.get<PendingSubscriptionListResponse>(
    `/internal/subscriptions/pending?${params.toString()}`
  )
  return response.data
}

export async function resendIntakeEmail(
  subscriptionId: string
): Promise<ResendIntakeResult> {
  const response = await internalClient.post<ResendIntakeResult>(
    `/internal/subscriptions/${subscriptionId}/resend-intake`
  )
  return response.data
}

/* --------------------------------------------------------------------------
   Metrics Types
   -------------------------------------------------------------------------- */

/** A money figure attributed to the currency it was billed in. Amounts across
 *  currencies are NOT FX-converted and must never be summed. */
export interface CurrencyAmount {
  currency: string
  amount: number
}

export interface OverviewMetrics {
  total_portals_live: number
  avg_days_to_golive: number | null
  active_providers_at_day30: number | null
  total_scripts_this_month: number
  /** USD only. Other currencies are in revenue_by_currency. */
  total_revenue_this_month: number
  revenue_by_currency: CurrencyAmount[]
}

export interface AtRiskPharmacy {
  pharmacy_id: string
  pharmacy_name: string
  slug: string
  days_since_golive: number
  flag_reason: 'no_submissions_14d' | 'low_providers_30d'
  provider_count: number
  prescription_count: number
}

export interface AtRiskListResponse {
  count: number
  pharmacies: AtRiskPharmacy[]
}

export interface ChurnedPharmacy {
  pharmacy_id: string
  pharmacy_name: string
  days_active: number
  total_scripts: number
  tier_at_cancellation: string
  cancellation_date: string
}

export interface TierBreakdown {
  tier: string
  count: number
}

export interface ChurnMetrics {
  churn_rate_90d: number
  churned_pharmacies: ChurnedPharmacy[]
  total_active_subscriptions: number
  active_by_tier: TierBreakdown[]
  /** USD only. Other currencies are in mrr_by_currency. */
  mrr: number
  mrr_by_currency: CurrencyAmount[]
}

/* --------------------------------------------------------------------------
   Metrics Functions
   -------------------------------------------------------------------------- */

export async function getOverviewMetrics(): Promise<OverviewMetrics> {
  const response = await internalClient.get<OverviewMetrics>(
    '/internal/metrics/overview'
  )
  return response.data
}

export async function getAtRiskPharmacies(): Promise<AtRiskListResponse> {
  const response = await internalClient.get<AtRiskListResponse>(
    '/internal/metrics/at-risk'
  )
  return response.data
}

export async function getChurnMetrics(): Promise<ChurnMetrics> {
  const response = await internalClient.get<ChurnMetrics>(
    '/internal/metrics/churn'
  )
  return response.data
}

/* --------------------------------------------------------------------------
   CSV Catalog Import Functions
   -------------------------------------------------------------------------- */

export async function downloadCsvTemplate(): Promise<Blob> {
  const response = await internalClient.get('/internal/catalog/csv-template', {
    responseType: 'blob',
  })
  return response.data
}

export async function importIntakeCatalogCsv(
  intakeId: string,
  file: File
): Promise<CsvImportResult> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await internalClient.post<CsvImportResult>(
    `/internal/intake/${intakeId}/import-catalog`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000, // longer timeout for file upload
    }
  )
  return response.data
}
